/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, useReducer, useEffect, useState, useRef, ReactNode, useCallback } from 'react'
import { toast } from 'sonner'
import type { CartContextType, Cart, CartItem, CartValidationResult, AddToCartOptions, CartPriceType, CartExpiryState } from '@/types/cart'
import type { Flight, FlightResult, MultiSliceFlight, Hotel, ItineraryResponse } from '@/types/travel'
import { supabase } from './supabase'
import { useAuth } from './auth'
import { resolveRevalidationRules, shouldRevertSpecialPrice } from './subscription-revalidation'

// VOY-406: cart/checkout TTL. Internal countdown — no visible timer in UI.
// Frontend clock is advisory; backend cart-checkout edge function is the
// authoritative gate (returns HTTP 410 CART_EXPIRED past CHECKOUT_TTL_SECONDS).
const CHECKOUT_TTL_SECONDS = Number(import.meta.env.VITE_CHECKOUT_TTL_SECONDS) || 900
const CHECKOUT_TTL_EXPIRING_THRESHOLD_SECONDS = 60

// Debug logging - only active in development
const isDev = import.meta.env.DEV
const debugLog = (...args: unknown[]) => isDev && console.log('[cart]', ...args)
const debugWarn = (...args: unknown[]) => isDev && console.warn('[cart]', ...args)

// Cart reducer actions
type CartAction =
  | { type: 'ADD_ITEM'; payload: { item: FlightResult | Hotel; type: 'flight' | 'hotel'; itinerary?: ItineraryResponse; options?: AddToCartOptions } }
  | { type: 'REMOVE_ITEM'; payload: { itemId: string } }
  | { type: 'UPDATE_QUANTITY'; payload: { itemId: string; quantity: number } }
  | { type: 'CLEAR_CART' }
  | { type: 'EXPIRE_CART' }
  | { type: 'SET_OPEN'; payload: { isOpen: boolean } }
  | { type: 'LOAD_CART'; payload: { cart: Cart } }
  | { type: 'SET_ITINERARY'; payload: { itinerary?: ItineraryResponse } }
  | { type: 'DEDUPLICATE' }

// VOY-406: apply TTL to a freshly computed Cart. Called on every mutation so
// expiresAt is pushed forward; ttlStartedAt is preserved across mutations.
function withRefreshedTtl(cart: Cart, prev?: Cart): Cart {
  if (cart.items.length === 0) {
    return { ...cart, expiresAt: undefined, ttlStartedAt: undefined }
  }
  const now = Date.now()
  return {
    ...cart,
    expiresAt: new Date(now + CHECKOUT_TTL_SECONDS * 1000).toISOString(),
    ttlStartedAt: prev?.ttlStartedAt || new Date(now).toISOString(),
  }
}

// VOY-406: derive the current expiry state from a cart's expiresAt.
export function computeExpiryState(cart: Cart, nowMs: number = Date.now()): { state: CartExpiryState; secondsRemaining: number } {
  if (cart.items.length === 0 || !cart.expiresAt) {
    return { state: 'idle', secondsRemaining: -1 }
  }
  const expiresMs = new Date(cart.expiresAt).getTime()
  const secondsRemaining = Math.max(0, Math.floor((expiresMs - nowMs) / 1000))
  if (secondsRemaining <= 0) return { state: 'expired', secondsRemaining: 0 }
  if (secondsRemaining <= CHECKOUT_TTL_EXPIRING_THRESHOLD_SECONDS) return { state: 'expiring', secondsRemaining }
  return { state: 'active', secondsRemaining }
}

// Cart reducer — exported for unit tests (VOY-406).
export function cartReducer(state: { cart: Cart; isOpen: boolean }, action: CartAction) {
  switch (action.type) {
    case 'ADD_ITEM': {
      const { item, type, itinerary, options } = action.payload
      
      // Debug logging (dev only)
      debugLog('ADD_ITEM action:', {
        type,
        itemId: item.id,
        itinerary: itinerary ? 'provided' : 'not provided',
        priceType: options?.priceType,
      });
      
      // Check if item already exists
      const existingItemIndex = state.cart.items.findIndex(
        cartItem => cartItem.id === item.id && cartItem.type === type
      )

      // Prevent duplicate adds
      if (existingItemIndex >= 0) {
        debugWarn('Item already in cart, not adding duplicate:', item.id);
        return state; // Don't add duplicates, just return current state
      }

      // Extract base price based on type.
      // For flights: multi-slice uses totalPrice, single-slice uses price.
      // Do NOT use `'totalPrice' in item` as discriminator — expandable-
      // flight-card.tsx:450 copies price into totalPrice on all flights at
      // add-to-cart time, making the `in` check always true. Use the type
      // field discriminator instead (same fix as cart-overlay.tsx #256).
      let basePrice: number;
      if (type === 'flight') {
        basePrice = (item as MultiSliceFlight).type === 'multi-slice'
          ? (item as MultiSliceFlight).totalPrice
          : (item as Flight).price || 0;
      } else {
        // Hotel - use totalPrice if available, otherwise fall back to price.amount
        const hotel = item as Hotel;
        if ((hotel as any).totalPrice && (hotel as any).totalPrice > 0) {
          basePrice = (hotel as any).totalPrice;
        } else if (typeof hotel.price === 'object') {
          basePrice = (hotel.price as any)?.amount || 0;
        } else {
          basePrice = hotel.price || 0;
        }
      }

      // Determine pricing - use options if provided, otherwise default to standard
      const priceType: CartPriceType = options?.priceType || 'standard'
      const standardPrice = options?.standardPrice ?? basePrice
      const specialPrice = options?.specialPrice
      
      // Applied price is based on price type
      const appliedPrice = priceType === 'special' && specialPrice !== undefined 
        ? specialPrice 
        : standardPrice
      
      // Debug: Log flight data being added to cart (dev only)
      if (type === 'flight') {
        debugLog('Adding flight with passenger data:', {
          flightId: item.id,
          hasPassengerData: !!(item as any).passengers
        });
      }
      
      // Create new item with proper duffelOfferId for flights
      const newItem: CartItem = {
        id: item.id,
        type,
        item,
        quantity: 1,
        price: appliedPrice,
        priceType,
        standardPrice,
        specialPrice,
        addedAt: new Date(),
        itinerary,
        // Store the true Duffel offer id when available
        ...(type === 'flight' ? { duffelOfferId: (item as any).duffelOfferId || (item as any).offerId || item.id } : {})
      }
      
      // Warn if flight is missing duffelOfferId
      if (type === 'flight' && !newItem.duffelOfferId) {
        console.error('WARNING: Flight missing duffelOfferId:', item);
      }

      const newItems = [...state.cart.items, newItem]
      const baseCart = calculateCartTotals(newItems)
      const withItinerary = itinerary ? { ...baseCart, itinerary } : baseCart
      const updatedCart = withRefreshedTtl(withItinerary, state.cart)

      debugLog('Cart updated - new item count:', newItems.length);
      return { ...state, cart: updatedCart }
    }

    case 'REMOVE_ITEM': {
      const newItems = state.cart.items.filter(item => item.id !== action.payload.itemId)
      const baseCart = calculateCartTotals(newItems)
      const withItinerary = state.cart.itinerary ? { ...baseCart, itinerary: state.cart.itinerary } : baseCart
      return { ...state, cart: withRefreshedTtl(withItinerary, state.cart) }
    }

    case 'UPDATE_QUANTITY': {
      const { itemId, quantity } = action.payload
      const newItems = state.cart.items.map(item =>
        item.id === itemId ? { ...item, quantity: Math.max(0, quantity) } : item
      ).filter(item => item.quantity > 0) // Remove items with 0 quantity

      const baseCart = calculateCartTotals(newItems)
      const withItinerary = state.cart.itinerary ? { ...baseCart, itinerary: state.cart.itinerary } : baseCart
      return { ...state, cart: withRefreshedTtl(withItinerary, state.cart) }
    }

    case 'CLEAR_CART':
    case 'EXPIRE_CART':
      return { ...state, cart: getEmptyCart() }

    case 'SET_OPEN':
      return { ...state, isOpen: action.payload.isOpen }

    case 'LOAD_CART': {
      debugLog('📄 LOAD_CART action:', action.payload.cart.items.length, 'items');
      // CRITICAL: Replace state entirely, don't merge
      return { ...state, cart: action.payload.cart };
    }

    case 'SET_ITINERARY':
      return { ...state, cart: { ...state.cart, itinerary: action.payload.itinerary } }

    case 'DEDUPLICATE': {
      const seen = new Map<string, boolean>();
      const uniqueItems = state.cart.items.filter(item => {
        const key = `${item.type}-${item.id}`;
        if (seen.has(key)) {
          console.warn('🗑️ Removing duplicate:', key);
          return false;
        }
        seen.set(key, true);
        return true;
      });
      
      debugLog('🧹 Deduplication: removed', state.cart.items.length - uniqueItems.length, 'duplicates');
      
      const newCart = calculateCartTotals(uniqueItems);
      newCart.itinerary = state.cart.itinerary;
      // VOY-406: preserve TTL across DEDUPLICATE. calculateCartTotals returns
      // a fresh Cart without expiresAt/ttlStartedAt, and DEDUPLICATE runs on
      // every LOAD_CART, so dropping these would reset the countdown on every
      // tab load and let users extend their cart indefinitely.
      newCart.expiresAt = state.cart.expiresAt;
      newCart.ttlStartedAt = state.cart.ttlStartedAt;
      return { ...state, cart: newCart };
    }

    default:
      return state
  }
}

// Helper function to calculate cart totals
function calculateCartTotals(items: CartItem[]): Cart {
  const totalPrice = items.reduce((sum, item) => sum + (item.price * item.quantity), 0)
  const flightCount = items.filter(item => item.type === 'flight').reduce((sum, item) => sum + item.quantity, 0)
  const hotelCount = items.filter(item => item.type === 'hotel').reduce((sum, item) => sum + item.quantity, 0)
  const itemCount = items.reduce((sum, item) => sum + item.quantity, 0)

  return {
    items,
    totalPrice,
    flightCount,
    hotelCount,
    itemCount
  }
}

// Exported for unit tests (VOY-406).
export function getEmptyCart(): Cart {
  return {
    items: [],
    totalPrice: 0,
    flightCount: 0,
    hotelCount: 0,
    itemCount: 0
  }
}

// Create context
const CartContext = createContext<CartContextType | undefined>(undefined)

// Cart provider component
export function CartProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth()
  const [state, dispatch] = useReducer(cartReducer, {
    cart: getEmptyCart(),
    isOpen: false
  })
  const [cartId, setCartId] = useState<string | null>(null)
  const lastAddRef = useRef<Record<string, number>>({})
  // VOY-406: TTL countdown state (internal; no visible timer in UI)
  const [expiryState, setExpiryState] = useState<CartExpiryState>('idle')
  const [secondsRemaining, setSecondsRemaining] = useState<number>(-1)
  // VOY-265: Flags to prevent saving to localStorage during initial load
  const isLoadingRef = useRef<boolean>(true)
  const hasLoadedRef = useRef<boolean>(false)
  // VOY-309: Ref survives async gap so multiple loadCart runs all respect clearCart
  const cartClearedRef = useRef<boolean>(false)

  // Load cart from Supabase or localStorage on mount
  useEffect(() => {
    const loadCart = async () => {
      // VOY-309: If success page cleared the cart, dispatch empty immediately and skip any async load.
      // Keep sessionStorage flag so reload/new tab also see empty (Supabase delete may still be in flight).
      const finalizeCartClear = () => {
        if (typeof localStorage !== 'undefined') localStorage.setItem('voy-cart', JSON.stringify(getEmptyCart()))
        cartClearedRef.current = false
        dispatch({ type: 'LOAD_CART', payload: { cart: getEmptyCart() } })
        hasLoadedRef.current = true
        isLoadingRef.current = false
      }
      if (cartClearedRef.current || (typeof sessionStorage !== 'undefined' && sessionStorage.getItem('voy-cart-cleared') === '1')) {
        finalizeCartClear()
        return
      }

      isLoadingRef.current = true

      // Before any dispatch, if clearCart was called in the meantime, dispatch empty (ref survives async).
      // Do not remove sessionStorage flag here so reload/new tab still see "cleared" until user adds to cart.
      const wasCleared = () => {
        if (cartClearedRef.current) return true
        if (typeof sessionStorage === 'undefined' || sessionStorage.getItem('voy-cart-cleared') !== '1') return false
        if (typeof localStorage !== 'undefined') {
          localStorage.setItem('voy-cart', JSON.stringify(getEmptyCart()))
        }
        cartClearedRef.current = false
        return true
      }
      
      // VOY-265: CRITICAL - Check localStorage FIRST before Supabase
      // This prevents Supabase empty cart from overwriting localStorage
      const savedCart = typeof localStorage !== 'undefined' ? localStorage.getItem('voy-cart') : null
      let localStorageCart: Cart | null = null
      
      if (savedCart) {
        try {
          const parsedCart = JSON.parse(savedCart)
          if (parsedCart.items && Array.isArray(parsedCart.items) && parsedCart.items.length > 0) {
            // VOY-406: lazy expiry on read — drop the cart if its TTL has elapsed.
            if (parsedCart.expiresAt && new Date(parsedCart.expiresAt).getTime() <= Date.now()) {
              debugLog('⌛ localStorage cart is expired, discarding');
              localStorage.setItem('voy-cart', JSON.stringify(getEmptyCart()))
            } else {
              localStorageCart = parsedCart
              debugLog('📦 Found cart in localStorage with', parsedCart.items.length, 'items');
            }
          }
        } catch (error) {
          console.error('Failed to parse localStorage cart:', error)
        }
      }

      if (user) {
        try {
          const { data: cartData, error: cartError } = await supabase
            .from('user_carts')
            .select('*, cart_items(*)')
            .eq('user_id', user.id)
            .order('updated_at', { ascending: false })
            .limit(1)
            .maybeSingle()

          if (cartError) {
            console.error('Error loading cart from Supabase:', cartError)
            // Fall through to localStorage
          } else if (cartData) {
            setCartId(cartData.id)
            const items: CartItem[] = cartData.cart_items?.map((item: any) => {
              const price = parseFloat(item.price)
              return {
                id: item.item_data.id,
                type: item.item_type,
                item: item.item_data,
                quantity: item.quantity,
                price,
                // Paywall fields - default to standard if not present (backward compatibility)
                priceType: item.price_type || 'standard' as CartPriceType,
                standardPrice: parseFloat(item.standard_price) || price,
                specialPrice: item.special_price ? parseFloat(item.special_price) : undefined,
                addedAt: new Date(item.added_at),
                itinerary: item.itinerary,
                // CRITICAL: Ensure duffelOfferId is set for flights - just use the item ID
                ...(item.item_type === 'flight' && item.item_data?.id 
                  ? { duffelOfferId: item.item_data.id } 
                  : {})
              }
            }) || []

            // VOY-265: If Supabase has no line items but localStorage does, prefer localStorage (recover after offline / failed sync).
            // Tradeoff: if the user emptied the cart on another device, this tab can still show stale items until localStorage syncs.
            // Follow-up: server-side cart_generation or cleared_at (or wipe local when server returns empty + explicit flag).
            if (items.length === 0 && localStorageCart && localStorageCart.items.length > 0) {
              debugLog('⚠️ Supabase cart is empty but localStorage has items - using localStorage');
              
              // SECURITY: Re-validate subscription status for localStorage cart
              // VOY-610: null when the plan is unknown — then nothing is reverted
              const rules = await resolveRevalidationRules(user?.id ?? null)
              
              const cartWithDates = {
                ...localStorageCart,
                items: localStorageCart.items.map((item: any) => {
                  const priceType = item.priceType || 'standard' as CartPriceType
                  const standardPrice = item.standardPrice || item.price
                  
                  // If item has special pricing but user no longer qualifies, revert to standard
                  const shouldRevertToStandard = shouldRevertSpecialPrice(priceType, rules)
                  if (shouldRevertToStandard) {
                    console.warn(`[cart] Reverting localStorage item ${item.id} to standard price - subscription no longer active`)
                  }
                  
                  return {
                    ...item,
                    addedAt: new Date(item.addedAt),
                    priceType: shouldRevertToStandard ? 'standard' : priceType,
                    price: shouldRevertToStandard ? standardPrice : item.price,
                    standardPrice,
                    specialPrice: item.specialPrice,
                    // Ensure duffelOfferId is set for flights
                    ...(item.type === 'flight' && !item.duffelOfferId ? { duffelOfferId: item.id } : {})
                  }
                })
              }
              // Early check + wasCleared() both needed: early check so multiple in-flight loadCart runs all dispatch empty; wasCleared() catches clearCart() called during async load
              if (cartClearedRef.current) {
                finalizeCartClear()
                return
              }
              dispatch({ type: 'LOAD_CART', payload: { cart: wasCleared() ? getEmptyCart() : cartWithDates } })
              dispatch({ type: 'DEDUPLICATE' })
              hasLoadedRef.current = true
              isLoadingRef.current = false
              return
            } else if (items.length > 0) {
              // Supabase has items - use it (even if localStorage also has items, Supabase is source of truth when it has data)
              // SECURITY: Re-validate subscription status and re-price items if needed
              // This prevents stale special prices if subscription expired
              // VOY-610: null when the plan is unknown — then nothing is reverted
              const rules = await resolveRevalidationRules(user?.id ?? null)
              
              const validatedItems = items.map(item => {
                // If item has special pricing but user no longer qualifies, revert to standard
                if (shouldRevertSpecialPrice(item.priceType, rules)) {
                  console.warn(`[cart] Reverting item ${item.id} to standard price - subscription no longer active`)
                  return {
                    ...item,
                    priceType: 'standard' as CartPriceType,
                    price: item.standardPrice // Use stored standard price
                  }
                }
                return item
              })
              
              const cart = calculateCartTotals(validatedItems)
              cart.itinerary = cartData.itinerary
              // VOY-406: hydrate TTL from user_carts.metadata.ttl
              const ttlMeta = (cartData.metadata as any)?.ttl as { expiresAt?: string; ttlStartedAt?: string } | undefined
              if (ttlMeta?.expiresAt) {
                cart.expiresAt = ttlMeta.expiresAt
                cart.ttlStartedAt = ttlMeta.ttlStartedAt
              }
              // Lazy expiry: if TTL has elapsed, treat as empty cart and drop the DB row
              if (cart.expiresAt && new Date(cart.expiresAt).getTime() <= Date.now()) {
                debugLog('⌛ Supabase cart is expired, discarding');
                try {
                  await supabase.from('cart_items').delete().eq('cart_id', cartData.id)
                  await supabase.from('user_carts').delete().eq('id', cartData.id)
                } catch (err) {
                  console.warn('[cart] Failed to delete expired Supabase cart:', err)
                }
                setCartId(null)
                if (typeof localStorage !== 'undefined') localStorage.setItem('voy-cart', JSON.stringify(getEmptyCart()))
                dispatch({ type: 'LOAD_CART', payload: { cart: getEmptyCart() } })
                hasLoadedRef.current = true
                isLoadingRef.current = false
                return
              }
              debugLog('📦 Loading cart from Supabase - items:', items.length);
              debugLog('Loading cart from Supabase - itinerary:', cartData.itinerary ? 'provided' : 'none');

              if (cartClearedRef.current) {
                finalizeCartClear()
                return
              }
              // CRITICAL: Replace state entirely and deduplicate
              dispatch({ type: 'LOAD_CART', payload: { cart: wasCleared() ? getEmptyCart() : cart } })
              dispatch({ type: 'DEDUPLICATE' })
              hasLoadedRef.current = true
              isLoadingRef.current = false
              return
            }
            // If Supabase cart is empty and localStorage is also empty, continue to use empty cart
            debugLog('⚠️ Both Supabase and localStorage are empty');
          }
        } catch (error) {
          console.error('Failed to load cart from Supabase:', error)
          // Fall through to localStorage
        }
      }

      // VOY-265: If we get here, either no user or Supabase failed/empty - use localStorage if available
      if (localStorageCart && localStorageCart.items.length > 0) {
        debugLog('📦 Loading cart from localStorage (no user or Supabase failed/empty)');
        
        // SECURITY: Re-validate subscription status for localStorage cart too
        // VOY-610: null when the plan is unknown — then nothing is reverted
        const rules = await resolveRevalidationRules(user?.id ?? null)
        
        const cartWithDates = {
          ...localStorageCart,
          items: localStorageCart.items.map((item: any) => {
            const priceType = item.priceType || 'standard' as CartPriceType
            const standardPrice = item.standardPrice || item.price
            
            // If item has special pricing but user no longer qualifies, revert to standard
            const shouldRevertToStandard = shouldRevertSpecialPrice(priceType, rules)
            if (shouldRevertToStandard) {
              console.warn(`[cart] Reverting localStorage item ${item.id} to standard price - subscription no longer active`)
            }
            
            return {
              ...item,
              addedAt: new Date(item.addedAt),
              priceType: shouldRevertToStandard ? 'standard' : priceType,
              price: shouldRevertToStandard ? standardPrice : item.price,
              standardPrice,
              specialPrice: item.specialPrice,
              // Ensure duffelOfferId is set for flights
              ...(item.type === 'flight' && !item.duffelOfferId ? { duffelOfferId: item.id } : {})
            }
          })
        }
        if (cartClearedRef.current) {
          finalizeCartClear()
          return
        }
        dispatch({ type: 'LOAD_CART', payload: { cart: wasCleared() ? getEmptyCart() : cartWithDates } })
        dispatch({ type: 'DEDUPLICATE' })
      }
      
      hasLoadedRef.current = true
      isLoadingRef.current = false
    }

    loadCart()
  }, [user])

  // Save cart to Supabase and localStorage whenever it changes
  useEffect(() => {
    // VOY-265: CRITICAL - Don't save to localStorage during initial load
    // This prevents empty Supabase cart from overwriting localStorage
    if (isLoadingRef.current || !hasLoadedRef.current) {
      debugLog('⏸️ Skipping save - cart is still loading');
      return
    }

    // Always save to localStorage (but only after initial load)
    if (typeof localStorage !== 'undefined') localStorage.setItem('voy-cart', JSON.stringify(state.cart))

    if (user && state.cart.items.length > 0) {
      const saveCart = async () => {
        try {
          // Verify user is authenticated before proceeding
          const { data: { session }, error: sessionError } = await supabase.auth.getSession()
          if (sessionError || !session) {
            console.error('User not authenticated, skipping cart save:', sessionError)
            return
          }

          let currentCartId = cartId

          // VOY-406: persist TTL alongside the cart so reloads/new tabs see the same deadline.
          const ttlMetadata = state.cart.expiresAt
            ? {
                ttl: {
                  expiresAt: state.cart.expiresAt,
                  ttlStartedAt: state.cart.ttlStartedAt,
                  ttlSeconds: CHECKOUT_TTL_SECONDS,
                },
              }
            : {}

          if (!currentCartId) {
            const { data: newCart, error: createError } = await supabase
              .from('user_carts')
              .insert({
                user_id: user.id,
                itinerary: state.cart.itinerary || null,
                metadata: ttlMetadata,
              })
              .select()
              .single()

            if (createError) throw createError
            currentCartId = newCart.id
            setCartId(currentCartId)
            
            // Verify cart was created and is accessible (for RLS policy verification)
            // Wait a bit to ensure the cart is fully committed to the database
            await new Promise(resolve => setTimeout(resolve, 100))
            
            const { data: verifyCart, error: verifyError } = await supabase
              .from('user_carts')
              .select('id, user_id')
              .eq('id', currentCartId)
              .single()
            
            if (verifyError || !verifyCart) {
              console.error('Cart verification failed:', { verifyError, verifyCart, currentCartId, userId: user.id })
              throw new Error('Failed to verify cart creation for RLS policy')
            }
            
            // Double-check that the cart belongs to the current user
            if (verifyCart.user_id !== user.id) {
              console.error('Cart user_id mismatch:', { cartUserId: verifyCart.user_id, currentUserId: user.id })
              throw new Error('Cart user_id does not match current user')
            }
          } else {
            // Verify existing cart is accessible before updating
            const { data: verifyCart, error: verifyError } = await supabase
              .from('user_carts')
              .select('id')
              .eq('id', currentCartId)
              .single()
            
            if (verifyError || !verifyCart) {
              // Cart doesn't exist or isn't accessible, create a new one
              const { data: newCart, error: createError } = await supabase
                .from('user_carts')
                .insert({
                  user_id: user.id,
                  itinerary: state.cart.itinerary || null,
                  metadata: ttlMetadata,
                })
                .select()
                .single()

              if (createError) throw createError
              currentCartId = newCart.id
              setCartId(currentCartId)
            } else {
              await supabase
                .from('user_carts')
                .update({
                  itinerary: state.cart.itinerary || null,
                  metadata: ttlMetadata,
                  updated_at: new Date().toISOString()
                })
                .eq('id', currentCartId)
            }
          }

          // Verify cart exists one more time before inserting items
          const { data: finalVerifyCart, error: finalVerifyError } = await supabase
            .from('user_carts')
            .select('id, user_id')
            .eq('id', currentCartId)
            .single()
          
          if (finalVerifyError || !finalVerifyCart) {
            console.error('Final cart verification failed before insert:', { 
              finalVerifyError, 
              finalVerifyCart, 
              currentCartId, 
              userId: user.id 
            })
            throw new Error('Cart verification failed before inserting items')
          }
          
          if (finalVerifyCart.user_id !== user.id) {
            console.error('Cart user_id mismatch before insert:', { 
              cartUserId: finalVerifyCart.user_id, 
              currentUserId: user.id 
            })
            throw new Error('Cart user_id does not match current user before insert')
          }

          // VOY-310: Insert first, delete old items only after insert succeeds. This avoids emptying the
          // cart in DB when insert fails (e.g. RLS 403). Brief window where old + new items coexist is
          // acceptable; delete runs immediately after insert. Concurrent saves could cause duplicates—
          // consider transaction/UPSERT if that becomes an issue.
          if (state.cart.items.length > 0) {
            const { data: existingRows } = await supabase
              .from('cart_items')
              .select('id')
              .eq('cart_id', currentCartId)

            const existingIds = (existingRows ?? []).map((r: { id: string }) => r.id)

            // Build items with pricing columns (added by migration 20251215130000)
            const itemsWithPricing = state.cart.items.map(item => ({
              cart_id: currentCartId,
              item_type: item.type,
              item_data: item.item,
              quantity: item.quantity,
              price: item.price.toString(),
              // Paywall pricing fields (may not exist if migration hasn't run)
              price_type: item.priceType || 'standard',
              standard_price: (item.standardPrice || item.price).toString(),
              special_price: item.specialPrice?.toString() || null,
              itinerary: item.itinerary || null,
              added_at: item.addedAt.toISOString()
              // Removed duffel_offer_id - store it in item_data instead
            }))

            // Try inserting with pricing columns first
            let { error: insertError } = await supabase
              .from('cart_items')
              .insert(itemsWithPricing)

            // TODO(remove by 2026-07-01): PGRST204 fallback for envs missing paywall columns (migration 20251215130000).
            // Drop this branch once all deployed DBs have the pricing columns.
            if (insertError && (insertError as any).code === 'PGRST204') {
              console.warn('[cart] Pricing columns not available, retrying without them');
              
              // Build items without pricing columns
              const itemsWithoutPricing = state.cart.items.map(item => ({
                cart_id: currentCartId,
                item_type: item.type,
                item_data: item.item,
                quantity: item.quantity,
                price: item.price.toString(),
                itinerary: item.itinerary || null,
                added_at: item.addedAt.toISOString()
              }))

              const retryResult = await supabase
                .from('cart_items')
                .insert(itemsWithoutPricing)

              insertError = retryResult.error;
            }

            if (insertError) {
              console.error('Failed to insert cart items:', {
                error: insertError,
                cartId: currentCartId,
                userId: user.id,
                itemCount: state.cart.items.length,
                sessionExists: !!session,
                cartExists: !!finalVerifyCart,
                cartUserId: finalVerifyCart?.user_id
              })
              throw insertError
            }

            // Only remove previous items after successful insert
            if (existingIds.length > 0) {
              const { error: deleteError } = await supabase
                .from('cart_items')
                .delete()
                .in('id', existingIds)
              if (deleteError) {
                console.warn('[cart] Failed to delete old cart items after insert:', deleteError)
              }
            }
          }
        } catch (error) {
          console.error('Failed to save cart to Supabase:', error)
        }
      }

      saveCart()
    } else if (user && cartId && state.cart.items.length === 0) {
      const deleteEmptyCart = async () => {
        try {
          await supabase
            .from('user_carts')
            .delete()
            .eq('id', cartId)
          setCartId(null)
        } catch (error) {
          console.error('Failed to delete empty cart:', error)
        }
      }
      deleteEmptyCart()
    }
  }, [state.cart, user, cartId])

  // VOY-406: drive the internal countdown off state.cart.expiresAt.
  // When expiry is reached, dispatch EXPIRE_CART, clear persistence, and set
  // a sessionStorage flag so the checkout page can react on its side.
  useEffect(() => {
    if (state.cart.items.length === 0 || !state.cart.expiresAt) {
      setExpiryState('idle')
      setSecondsRemaining(-1)
      return
    }

    let cancelled = false

    const expireCart = () => {
      if (cancelled) return
      debugLog('⌛ Cart TTL elapsed — expiring')
      if (typeof sessionStorage !== 'undefined') sessionStorage.setItem('voy-cart-expired', '1')
      if (typeof localStorage !== 'undefined') localStorage.setItem('voy-cart', JSON.stringify(getEmptyCart()))
      dispatch({ type: 'EXPIRE_CART' })
      // Clean up Supabase cart fire-and-forget (same pattern as clearCart)
      if (user && cartId) {
        const cartIdToDelete = cartId
        setCartId(null)
        void (async () => {
          try {
            await supabase.from('cart_items').delete().eq('cart_id', cartIdToDelete)
            await supabase.from('user_carts').delete().eq('id', cartIdToDelete)
          } catch (err) {
            console.error('[cart] Failed to clear expired Supabase cart:', err)
          }
        })()
      }
    }

    const tick = () => {
      const { state: next, secondsRemaining: nextSeconds } = computeExpiryState(state.cart)
      setExpiryState(next)
      setSecondsRemaining(nextSeconds)
      if (next === 'expired') expireCart()
    }

    tick()
    const interval = setInterval(tick, 1000)
    const onVisibility = () => { if (document.visibilityState === 'visible') tick() }
    document.addEventListener('visibilitychange', onVisibility)

    return () => {
      cancelled = true
      clearInterval(interval)
      document.removeEventListener('visibilitychange', onVisibility)
    }
  }, [state.cart, user, cartId])

  const addToCart = (
    item: FlightResult | Hotel, 
    type: 'flight' | 'hotel', 
    itinerary?: ItineraryResponse,
    options?: AddToCartOptions
  ) => {
    // Throttle rapid duplicate adds of the same item
    try {
      const key = `${type}:${(item as any)?.id}`
      const now = Date.now()
      const last = lastAddRef.current[key] || 0
      if (now - last < 1200) {
        debugLog('throttled addToCart', { key, deltaMs: now - last })
        return
      }
      lastAddRef.current[key] = now
    } catch (err) {
      debugWarn('throttle addToCart error', err)
    }
    
    // VOY-309: clear "cart was cleared after checkout" so we can load this new cart on reload
    if (typeof sessionStorage !== 'undefined') sessionStorage.removeItem('voy-cart-cleared')
    // VOY-406: clear prior expiry flag so a new cart isn't treated as just-expired
    if (typeof sessionStorage !== 'undefined') sessionStorage.removeItem('voy-cart-expired')
    cartClearedRef.current = false
    debugLog('addToCart called with itinerary:', itinerary ? 'provided' : 'none', 'options:', options);
    dispatch({ type: 'ADD_ITEM', payload: { item, type, itinerary, options } })
    
    // Show toast notification
    const itemName = type === 'flight' 
      ? 'slices' in item && Array.isArray(item.slices)
        ? `${item.slices[0].origin} → ${item.slices[item.slices.length - 1].destination}`
        : `${(item as any).origin} → ${(item as any).destination}`
      : (item as Hotel).name
    
    // Show pricing info in toast if special price
    const priceInfo = options?.priceType === 'special' 
      ? ' at special subscriber price'
      : ''
    
    toast.success(`${itemName} added to cart${priceInfo}`, {
      description: `You now have ${state.cart.itemCount + 1} item${state.cart.itemCount === 0 ? '' : 's'} in your cart`,
    })
  }

  const removeFromCart = (itemId: string) => {
    dispatch({ type: 'REMOVE_ITEM', payload: { itemId } })
  }

  const updateQuantity = (itemId: string, quantity: number) => {
    dispatch({ type: 'UPDATE_QUANTITY', payload: { itemId, quantity } })
  }

  const clearCart = () => {
    dispatch({ type: 'CLEAR_CART' })
    cartClearedRef.current = true
    // Signal to loadCart to not overwrite with stale data when landing on success page
    if (typeof sessionStorage !== 'undefined') sessionStorage.setItem('voy-cart-cleared', '1')
    // Persist empty cart immediately so loadCart (if still in progress) or next load sees it
    if (typeof localStorage !== 'undefined') localStorage.setItem('voy-cart', JSON.stringify(getEmptyCart()))
    // VOY-309: Clear Supabase cart immediately so reload/new tab sees empty (don't wait for save effect)
    if (user && cartId) {
      const cartIdToDelete = cartId
      setCartId(null) // Synchronous so save effect and UI don't use stale cartId
      // Fire-and-forget: delete in Supabase so new tab/reload sees empty; errors logged only
      void (async () => {
        try {
          await supabase.from('cart_items').delete().eq('cart_id', cartIdToDelete)
          await supabase.from('user_carts').delete().eq('id', cartIdToDelete)
        } catch (err) {
          console.error('[cart] Failed to clear Supabase cart:', err)
        }
      })()
    }
  }

  const openCart = () => {
    dispatch({ type: 'SET_OPEN', payload: { isOpen: true } })
  }

  const closeCart = () => {
    dispatch({ type: 'SET_OPEN', payload: { isOpen: false } })
  }

  const toggleCart = () => {
    dispatch({ type: 'SET_OPEN', payload: { isOpen: !state.isOpen } })
  }

  const setItinerary = (itinerary: ItineraryResponse | undefined) => {
    dispatch({ type: 'SET_ITINERARY', payload: { itinerary } })
  }

  /**
   * Validate the entire cart against subscription rules
   * Checks price types, ticket limits, and beneficiary restrictions
   */
  const validateCart = useCallback(async (): Promise<CartValidationResult> => {
    try {
      // Get current subscription and rules
      const rules = await resolveRevalidationRules(user?.id ?? null)
      if (!rules) {
        // VOY-610: plan unknown (lookup failed) — never treat that as Free. Fail open like the
        // catch below; cart-checkout re-quotes with the server-side tier.
        return { isValid: true }
      }

      // Check for special-priced items without subscription
      const specialPricedItems = state.cart.items.filter(item => item.priceType === 'special')
      if (specialPricedItems.length > 0 && !rules.canHaveSpecialPrice) {
        return {
          isValid: false,
          error: 'Your cart contains special-priced items but you do not have an active subscription.',
          upgradeRequired: true,
          suggestedTier: 'pro',
          blockedItems: specialPricedItems
        }
      }

      // Check ticket limit
      const totalItems = state.cart.items.reduce((sum, item) => sum + item.quantity, 0)
      if (rules.ticketLimit !== null && totalItems > rules.ticketLimit) {
        return {
          isValid: false,
          error: `Maximum ${rules.ticketLimit} items allowed per booking. You have ${totalItems} items.`,
          upgradeRequired: false
        }
      }

      // Check beneficiary policy for self-only tiers
      if (rules.beneficiaryPolicy === 'self-only' && rules.canHaveSpecialPrice) {
        // For now, we'll validate this at checkout when passenger info is provided
        // Here we just ensure the cart structure is valid
      }

      return { isValid: true }
    } catch (error) {
      console.error('[cart] Error validating cart:', error)
      // On error, allow cart to proceed (fail open)
      return { isValid: true }
    }
  }, [state.cart.items, user])

  const value: CartContextType = {
    cart: state.cart,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    isOpen: state.isOpen,
    openCart,
    closeCart,
    toggleCart,
    setItinerary,
    validateCart,
    // VOY-406: TTL exposure (internal; consumers decide side-effects)
    expiryState,
    expiresAt: state.cart.expiresAt,
    secondsRemaining,
  }

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  )
}

// Hook to use cart context
export function useCart() {
  const context = useContext(CartContext)
  if (context === undefined) {
    throw new Error('useCart must be used within a CartProvider')
  }
  return context
}